/**
 * Site health card (AB#1033 / AB#418): the SEO & AEO checklist as one calm card.
 * Self-contained so it can move from the Search page to the Dashboard (AB#413).
 *
 * Fix-in-place: a `fix` step folds away while the mutation runs; the OLD payload
 * stays on screen until both the fold (250 ms) and the request are done, then the
 * new payload swaps in — the arc sweeps to its new offset and the sentence fades.
 * Reduce Motion skips the fold: the swap happens in one frame when the request lands.
 *
 * Free-first (AB#418, 2026-10-19): no paid SEO sources in the UI. `info` rows
 * (e.g. "You show up on Google's first page for 4 searches") only appear in the list.
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Check, Circle, Clock, Info } from 'lucide-react';
import { useSiteHealth, useSiteHealthFix, type SiteHealth, type SiteHealthRow } from '@wordpuppi/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';

const FOLD_MS = 250; // matches .site-health-step in index.css
const ARC = 2 * Math.PI * 30; // r=30 → 188.5

/** Same rule as the `motion-reduce:` variant: OS setting, unless Appearance says "always". */
function reducedMotion() {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
    document.documentElement.getAttribute('data-motion') !== 'always'
  );
}

export function SiteHealthCard({ siteSlug }: { siteSlug: string }) {
  const { data: res, isLoading, isError } = useSiteHealth(siteSlug);
  const fix = useSiteHealthFix(siteSlug);
  const navigate = useNavigate();
  // While a fix runs, `frozen` pins the payload the user clicked on.
  const [frozen, setFrozen] = useState<SiteHealth | null>(null);
  const [busy, setBusy] = useState<{ id: string; leaving: boolean } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  if (isLoading) return <SiteHealthSkeleton />;
  const health = frozen ?? res?.data;
  if (isError || !health) {
    return <p className="text-sm text-muted-foreground">Couldn't load your site health right now.</p>;
  }

  const run = async (row: SiteHealthRow) => {
    const action = row.action;
    if (!action || busy) return;
    if (action.kind === 'link') {
      // Links may carry a query (`/posts/new?title=…`); the router wants it as `search`.
      const [to, query] = action.to.split('?');
      // Already on that page (e.g. "Connect Google" on Search): jump to the section instead of a no-op navigate.
      const here = document.getElementById('search-console');
      if (to === window.location.pathname && here) {
        here.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
        here.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
        return;
      }
      navigate(query ? { to, search: Object.fromEntries(new URLSearchParams(query)) as never } : { to });
      return;
    }
    const instant = reducedMotion();
    setFailed(null);
    setFrozen(health);
    setBusy({ id: row.id, leaving: !instant });
    const fold = new Promise((r) => setTimeout(r, instant ? 0 : FOLD_MS));
    try {
      // onSuccess has already written the new payload into the query cache.
      await Promise.all([fix.mutateAsync(action.fix), fold]);
    } catch {
      setFailed(row.id);
    }
    setFrozen(null);
    setBusy(null);
  };

  const { status, next_steps, rows } = health;
  const offset = ARC * (1 - (status.total ? status.passed / status.total : 0));
  const label = `${status.passed} of ${status.total} checks passed`;

  return (
    <Card aria-label="Site health" role="region" className="flex flex-col gap-6 p-6 sm:p-7">
      <div className="flex items-center gap-5">
        <svg width="72" height="72" viewBox="0 0 72 72" role="img" aria-label={label} className="shrink-0">
          <circle cx="36" cy="36" r="30" fill="none" strokeWidth="6" className="stroke-muted" />
          <circle
            cx="36" cy="36" r="30" fill="none" strokeWidth="6" strokeLinecap="round"
            strokeDasharray={ARC} strokeDashoffset={offset} transform="rotate(-90 36 36)"
            className="site-health-arc stroke-primary"
          />
          <text x="36" y="41" textAnchor="middle" fontSize="16" fontWeight="600" className="fill-foreground">
            {status.passed}/{status.total}
          </text>
        </svg>
        <div className="flex min-w-0 flex-col gap-1">
          <div className="text-[13px] font-medium text-muted-foreground">Site health</div>
          {/* Stable live region; the keyed child remounts so the fade replays on change. */}
          <div aria-live="polite">
            <div key={status.sentence} className="site-health-fade text-xl font-semibold leading-tight tracking-[-0.01em] sm:text-[26px]">
              {status.sentence}
            </div>
          </div>
          <div className="text-[13px] text-muted-foreground">{label}</div>
        </div>
      </div>

      {next_steps.length > 0 ? (
        <div className="flex flex-col">
          <div className="pb-2 text-[13px] font-medium text-muted-foreground">Do this next</div>
          {next_steps.slice(0, 3).map((row, i) => (
            <div key={row.id} className="site-health-step" data-leaving={busy?.id === row.id && busy.leaving ? '' : undefined}>
              <div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-t py-3.5 sm:flex-nowrap">
                  <div className="flex min-w-0 flex-[1_1_16rem] items-start gap-2">
                    <span className="text-[15px] leading-[1.45]">{row.title}</span>
                    {row.hint && (
                      // A click/tap/keyboard popover, not `title`: native tooltips never show on
                      // touch or focus, so the hint was mouse-only. before: pads the hit area to 32px.
                      <Popover>
                        <PopoverTrigger
                          render={
                            <button
                              type="button"
                              aria-label={`Why this matters: ${row.hint}`}
                              className="relative mt-0.5 flex size-5 shrink-0 cursor-help items-center justify-center rounded-full border bg-background text-xs leading-none text-muted-foreground outline-none focus-ring before:absolute before:-inset-1.5"
                            />
                          }
                        >
                          ?
                        </PopoverTrigger>
                        <PopoverContent side="top" className="w-64 p-3 text-sm text-pretty">
                          {row.hint}
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                  {row.action && (
                    <Button
                      variant={i === 0 ? 'default' : 'outline'}
                      className="shrink-0 rounded-[10px]"
                      disabled={!!busy}
                      onClick={() => run(row)}
                    >
                      {row.action.label}
                    </Button>
                  )}
                </div>
                {failed === row.id && (
                  <p className="pb-3 text-[13px] text-muted-foreground" role="status">
                    We couldn't fix that. Please try again.
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="site-health-fade flex items-center gap-3 rounded-[10px] bg-muted p-4">
          <Check className="size-[22px] shrink-0" aria-hidden="true" />
          <p className="text-[15px] leading-[1.45]">
            Nothing left to fix — nice work. We'll tell you here if anything changes.
          </p>
        </div>
      )}

      <details className="border-t pt-3.5">
        <summary className="cursor-pointer rounded-sm text-[15px] font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
          See everything
        </summary>
        <ul className="flex flex-col gap-2.5 pt-3">
          {rows.map((row) => (
            <li key={row.id} className={`flex items-center gap-2.5 text-[15px] ${row.pending ? 'text-muted-foreground' : ''}`}>
              {row.pending ? (
                <Clock className="size-4 shrink-0" role="img" aria-label="Coming soon" />
              ) : row.info ? (
                // Info rows aren't counted in passed/total, so never show them as a passed check.
                <Info className="size-4 shrink-0 text-muted-foreground" role="img" aria-label="For your information" />
              ) : row.ok ? (
                <Check className="size-4 shrink-0" strokeWidth={2.2} role="img" aria-label="Passed" />
              ) : (
                <Circle className="size-4 shrink-0 text-amber-700 dark:text-amber-400" strokeWidth={2.2} role="img" aria-label="Needs a fix" />
              )}
              <span>{row.title}</span>
            </li>
          ))}
        </ul>
      </details>
    </Card>
  );
}

/** Roughly the card's resting shape, so the page doesn't jump when it lands. */
function SiteHealthSkeleton() {
  return (
    <Card aria-busy="true" aria-label="Loading site health" className="flex flex-col gap-6 p-6 sm:p-7">
      <div className="flex items-center gap-5">
        <Skeleton className="size-[72px] shrink-0 rounded-full" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-7 w-3/4" />
          <Skeleton className="h-3 w-32" />
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
      <Skeleton className="h-5 w-48" />
    </Card>
  );
}
